import React, { useContext } from 'react';

import EditorPanelIcon from '@atlaskit/icon/glyph/editor/panel';

import { Flex } from '@qality/common-components/src/atoms/Flex';
import {
  Table,
  createColumnWidths,
  createColumnsWidths,
} from '@qality/common-components/src/atoms/Table';
import { Text } from '@qality/common-components/src/atoms/Text';
import { FEATURE_FLAG } from '@qality/common-config';
import { Project } from '@qality/common-model';
import { QAlitySettingsContext } from '@qality/common-state/src/QAlitySettingsProvider';

import { ContentWithIconTooltip } from '../../molecules/ContentWithIconTooltip';
import { ProjectSettingsRow } from './ProjectSettings.row';
import { ProjectSettingsRowToggleAll } from './ProjectSettings.rowToggleAll';

const APP_NAME = FEATURE_FLAG.isPremium ? 'QAlity Plus' : 'QAlity';
const ENABLE_ISSUE_TYPE_TOOLTIP =
  'In order to use QAlity, Jira administrator needs to ensure that QAlity ' +
  'Test issue type is enabled for this project. It can be easliy enabled or removed here.';
const ENABLE_APP_VISIBILITY_IN_ALL_ISSUES_TOOLTIP =
  'When enabled, QAlity will show in every Jira issue in this project. ' +
  'When disabled, QAlity will only be displayed in the dedicated issue type - QAlity Test.';
const ENABLE_PLUGIN_TOOLTIP =
  'When QAlity is disabled, you won’t see it in a project and in project filters.';

type Columns =
  | 'project'
  | 'appEnableInProject'
  | 'appVisiblityInIssues'
  | 'appEnableIssueType';

const ColumnsWidths = createColumnsWidths<Columns>({
  project: createColumnWidths('400px', '300px', '600px'),
  appEnableInProject: createColumnWidths('200px', '150px', '200px'),
  appVisiblityInIssues: createColumnWidths('200px', '150px', '200px'),
  appEnableIssueType: createColumnWidths('200px', '150px', '200px'),
});

export const ProjectSettingsView: React.FC = () => {
  const { projects } = useContext(QAlitySettingsContext);

  const infoMessage =
    projects.length <= 0 ? 'There are no projects to display' : '';

  return (
    <Table data-testid='TestCyclesOverviewTableView-Table' addStickyHeader>
      <Table.Head>
        <Table.Tr>
          <Table.Th widths={ColumnsWidths.project}>Project</Table.Th>
          <Table.Th widths={ColumnsWidths.appEnableInProject}>
            <ContentWithIconTooltip
              tooltipContent={ENABLE_PLUGIN_TOOLTIP}
              iconAfter={
                <EditorPanelIcon label='editor-panel-icon' size='small' />
              }
            >
              Enable {APP_NAME} in project
            </ContentWithIconTooltip>
          </Table.Th>
          <Table.Th widths={ColumnsWidths.appVisiblityInIssues}>
            <ContentWithIconTooltip
              tooltipContent={ENABLE_APP_VISIBILITY_IN_ALL_ISSUES_TOOLTIP}
              iconAfter={
                <EditorPanelIcon label='editor-panel-icon' size='small' />
              }
            >
              App visibility in all issues
            </ContentWithIconTooltip>
          </Table.Th>
          <Table.Th widths={ColumnsWidths.appEnableIssueType}>
            <ContentWithIconTooltip
              tooltipContent={ENABLE_ISSUE_TYPE_TOOLTIP}
              iconAfter={
                <EditorPanelIcon label='editor-panel-icon' size='small' />
              }
            >
              Enable {APP_NAME} issue type
            </ContentWithIconTooltip>
          </Table.Th>
        </Table.Tr>
      </Table.Head>

      <Table.Body>
        {infoMessage && (
          <Table.Tr>
            <Table.Td colSpan={4}>
              <Flex justifyCenter paddingSize='medium'>
                <Text>{infoMessage}</Text>
              </Flex>
            </Table.Td>
          </Table.Tr>
        )}
        {!infoMessage && (
          <>
            <ProjectSettingsRowToggleAll />
            {projects.map((project, index) => {
              return (
                <ProjectSettingsRow
                  key={`${project.id}-${index}`}
                  project={new Project(project)}
                  rowIndex={index}
                />
              );
            })}
          </>
        )}
      </Table.Body>
    </Table>
  );
};
